import { useEffect, useState, useRef } from "react"
import { MyDispatch, MySelector } from "../redux/store"
import { useDispatch } from "react-redux"
import FullScreenLoader from "../components/utils/FullScreenLoader"
import GalleryFolderSelectionBar from "../components/navs/GalleryFolderSelectionBar"
import { HandleGalleryFns } from "../hooks/handleGalleryFns"
import NoFiles from "../components/gallery/NoFiles"
import GalleryFolderSelectionPhone from "../components/navs/GalleryFolderSelectionPhone"
import {
  resetSelectedPhotos,
  setCurrentFolder as setCurrentFolderType,
  setCurrentFolderData,
  setAiSearchActive,
} from "../redux/utilSlice"
import AISearchInterface from "../components/gallery/AISearchInterface"
import { resolveGalleryDesign } from "../gallery-designs/registry"
import { GalleryContentMode } from "../gallery-designs/types"

const Gallery = () => {
  const dispatch = useDispatch<MyDispatch>()
  const { data, eventId, category } = MySelector((state) => state.event)
  const { allFiles } = MySelector((state) => state.gallery)
  const { currentFolderData, selectionMode, aiSearchData, navHeight } =
    MySelector((state) => state.util)

  const { setAllPhotos, setFolderFiles, folders } = HandleGalleryFns()

  const [currentFolder, setCurrentFolder] = useState<string>("All Photos")
  const isInitialLoad = useRef(true)

  // Keep the loader up until the (single) cover image has painted. The gallery
  // renders underneath so its own <img> is the one and only cover request.
  const cover = data?.coverPhotoLink
  const [coverLoaded, setCoverLoaded] = useState(false)

  const setFolderAndReset = (name: string) => {
    setCurrentFolder(name)
    dispatch(resetSelectedPhotos())
  }

  // Handle AI search results - set data for the layout when results are available
  useEffect(() => {
    if (aiSearchData.results.length > 0 && currentFolder === "AI Search") {
      dispatch(setCurrentFolderData(aiSearchData.results))
    }
  }, [aiSearchData.results, currentFolder, dispatch])

  // Initialize folder data when all dependencies are ready
  useEffect(() => {
    if (!allFiles) return

    if (data?.facePrivacy && category === "guest") {
      setCurrentFolder("AI Search")
      dispatch(setCurrentFolderType("common"))
      dispatch(setCurrentFolderData([]))
      dispatch(setAiSearchActive(true))
      return
    }

    if (folders === undefined) return

    // Only auto-select folder on initial load, not when files change
    if (isInitialLoad.current) {
      if (folders && folders.length > 0) {
        setCurrentFolder(folders[0].name)
        setFolderFiles(folders[0].id)
        dispatch(setCurrentFolderType("custom"))
      } else {
        setCurrentFolder("All Photos")
        setAllPhotos()
        dispatch(setCurrentFolderType("common"))
      }
      isInitialLoad.current = false
    }
  }, [
    allFiles,
    folders,
    data?.facePrivacy,
    category,
    dispatch,
    setAllPhotos,
    setFolderFiles,
  ])

  // Overlay the loader (rather than returning early) so the gallery mounts
  // underneath and its cover <img> starts loading while the loader is shown.
  const showLoader = !allFiles || (cover && !coverLoaded)

  const showAI =
    ((data?.facePrivacy && category === "guest") ||
      currentFolder === "AI Search") &&
    aiSearchData.aiSearchEnabled

  const contentMode: GalleryContentMode = showAI
    ? "ai"
    : Array.isArray(currentFolderData) && currentFolderData?.length > 0
    ? "photos"
    : "empty"

  const folderBar = (
    <>
      {/* Scroll anchor: folder/AI changes land here (below the navbar) instead
          of at the very top of the page (the cover). */}
      <div id="gallery-content-top" style={{ scrollMarginTop: navHeight }} />
      {/* Laptop */}
      <GalleryFolderSelectionBar
        currentFolder={currentFolder}
        setFolderAndReset={setFolderAndReset}
      />
      {/* Phone */}
      <GalleryFolderSelectionPhone
        currentFolder={currentFolder}
        setFolderAndReset={setFolderAndReset}
      />
    </>
  )

  // Pick the gallery design from the event's galleryTheme (falls back to default).
  const DesignComponent = resolveGalleryDesign(data?.galleryTheme)

  return (
    <div
      className={`flex flex-col flex-1 ${
        selectionMode ? "-mt-[25.5px]" : "mt-[40px]"
      } lg:mt-0`}
    >
      {showLoader && <FullScreenLoader />}
      <DesignComponent
        data={data}
        eventId={eventId}
        folderBar={folderBar}
        contentMode={contentMode}
        aiContent={<AISearchInterface setFolderAndReset={setFolderAndReset} />}
        emptyContent={<NoFiles />}
        photos={Array.isArray(currentFolderData) ? currentFolderData : []}
        onCoverLoad={() => setCoverLoaded(true)}
      />
    </div>
  )
}

export default Gallery
